'use client';

import { useEffect, useRef, useState } from 'react';
import { PDFDocument, degrees } from 'pdf-lib';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type PdfJsViewport = {
  width: number;
  height: number;
};

type PdfJsPage = {
  getViewport: (params: { scale: number }) => PdfJsViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: PdfJsViewport;
  }) => { promise: Promise<void> };
  cleanup?: () => void;
};

type PdfJsDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  cleanup?: () => void;
  destroy?: () => Promise<void> | void;
};

type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (params: {
    data: Uint8Array;
    disableWorker: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
  }) => {
    promise: Promise<PdfJsDocument>;
  };
};

type OrganizerPage = {
  id: string;
  sourceIndex: number;
  pageNumber: number;
  previewUrl: string;
  rotation: 0 | 90 | 180 | 270;
};

type OrganizerSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  pages: OrganizerPage[];
};

type LoadProgress = {
  current: number;
  total: number;
  label: string;
};

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;

function loadPdfJsModule() {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJsModule>;
  }

  return pdfJsModulePromise.then((module) => {
    if (!module.GlobalWorkerOptions.workerSrc) {
      module.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }

    return module;
  });
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return `cleanor.app-organize-pdf-${pageLabel}-${formatExportTimestamp(new Date())}.pdf`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

async function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The PDF page preview could not be created.'));
      },
      'image/jpeg',
      quality,
    );
  });
}

async function renderPagePreview(page: PdfJsPage) {
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.max(
    0.45,
    Math.min(0.8, 220 / Math.max(baseViewport.width, baseViewport.height)),
  );
  const viewport = page.getViewport({ scale });
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not render the PDF preview.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: context, canvas, viewport }).promise;
  const blob = await canvasToJpegBlob(canvas, 0.82);
  canvas.width = 0;
  canvas.height = 0;
  return URL.createObjectURL(blob);
}

async function readOrganizerSource(
  file: File,
  onProgress: (progress: LoadProgress) => void,
): Promise<OrganizerSource> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdfjs = await loadPdfJsModule();
  const loadingTask = pdfjs.getDocument({
    data: bytes,
    disableWorker: true,
    useSystemFonts: true,
    isEvalSupported: false,
  });

  let pdfDocument: PdfJsDocument | null = null;

  try {
    pdfDocument = await loadingTask.promise;
    const pages: OrganizerPage[] = [];

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
      onProgress({
        current: pageNumber,
        total: pdfDocument.numPages,
        label: `Preparing preview ${pageNumber} of ${pdfDocument.numPages}…`,
      });

      const page = await pdfDocument.getPage(pageNumber);

      try {
        pages.push({
          id: crypto.randomUUID(),
          sourceIndex: pageNumber - 1,
          pageNumber,
          previewUrl: await renderPagePreview(page),
          rotation: 0,
        });
      } finally {
        page.cleanup?.();
      }
    }

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: pdfDocument.numPages,
      pages,
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    pdfDocument?.cleanup?.();
    await pdfDocument?.destroy?.();
  }
}

function getNetRotation(rotation: OrganizerPage['rotation'], delta: -90 | 90) {
  const next = (rotation + delta + 360) % 360;
  return next as OrganizerPage['rotation'];
}

export default function BrowserPdfPageOrganizer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<OrganizerSource | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState<LoadProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }

      if (source) {
        source.pages.forEach((page) => URL.revokeObjectURL(page.previewUrl));
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
  }

  function replaceSource(nextSource: OrganizerSource | null) {
    setSource((current) => {
      if (current) {
        current.pages.forEach((page) => URL.revokeObjectURL(page.previewUrl));
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to organize.');
      return;
    }

    setIsPreparing(true);
    setProgress({ current: 0, total: 1, label: 'Opening PDF…' });

    try {
      const nextSource = await readOrganizerSource(file, setProgress);
      replaceSource(nextSource);
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    } finally {
      setIsPreparing(false);
      setProgress(null);
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();
    setProgress(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function movePage(pageId: string, direction: -1 | 1) {
    setSource((current) => {
      if (!current) {
        return current;
      }

      const index = current.pages.findIndex((item) => item.id === pageId);

      if (index < 0) {
        return current;
      }

      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= current.pages.length) {
        return current;
      }

      const nextPages = [...current.pages];
      const [item] = nextPages.splice(index, 1);
      nextPages.splice(nextIndex, 0, item);

      return {
        ...current,
        pages: nextPages,
      };
    });
    resetExportState();
    setCreditMessage(null);
    setErrorMessage(null);
  }

  function rotatePage(pageId: string, direction: -90 | 90) {
    setSource((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        pages: current.pages.map((page) =>
          page.id === pageId
            ? { ...page, rotation: getNetRotation(page.rotation, direction) }
            : page,
        ),
      };
    });
    resetExportState();
    setCreditMessage(null);
    setErrorMessage(null);
  }

  function removePage(pageId: string) {
    setSource((current) => {
      if (!current) {
        return current;
      }

      const nextPages = current.pages.filter((page) => page.id !== pageId);

      return {
        ...current,
        pages: nextPages,
      };
    });
    resetExportState();
    setCreditMessage(null);
    setErrorMessage(null);
  }

  async function saveOrganizedPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (!source.pages.length) {
      setErrorMessage('Keep at least one page in the output PDF.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({
      current: 0,
      total: source.pages.length,
      label: 'Checking credits and preparing the export…',
    });

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'organize-pdf-pages' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF organize export right now.';
        throw new Error(message);
      }

      const sourcePdf = await PDFDocument.load(await source.file.arrayBuffer());
      const outputPdf = await PDFDocument.create();
      const copiedPages = await outputPdf.copyPages(
        sourcePdf,
        source.pages.map((page) => page.sourceIndex),
      );

      copiedPages.forEach((copiedPage, index) => {
        setProgress({
          current: index + 1,
          total: copiedPages.length,
          label: `Applying page changes ${index + 1} of ${copiedPages.length}…`,
        });

        const originalRotation = copiedPage.getRotation().angle;
        const nextRotation = (originalRotation + source.pages[index].rotation) % 360;
        copiedPage.setRotation(degrees(nextRotation));
        outputPdf.addPage(copiedPage);
      });

      setProgress({
        current: copiedPages.length,
        total: copiedPages.length,
        label: 'Packing the organized PDF…',
      });

      const organizedBytes = Uint8Array.from(await outputPdf.save({ useObjectStreams: true }));
      const blob = new Blob([organizedBytes.buffer], { type: 'application/pdf' });
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextDownloadName = createExportFileName(source.pages.length);

      resetExportState();
      downloadUrlRef.current = nextDownloadUrl;
      setResultSize(blob.size);
      triggerBrowserDownload(nextDownloadUrl, nextDownloadName);

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Organized PDF download started. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Organized PDF download started. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Organized PDF download started.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF could not be organized in the browser.',
      );
    } finally {
      setProgress(null);
      setIsSaving(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Reorder, rotate, and delete PDF pages</h2>
          <p>
            Upload one PDF, rearrange the page stack, rotate pages, remove the ones you do not need,
            and export a cleaned-up PDF directly in the browser.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source
                  ? `${source.pages.length} page${source.pages.length === 1 ? '' : 's'} in the current output`
                  : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} source size • edit order before export`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || !source.pages.length || isPreparing || isSaving}
              onClick={() => void saveOrganizedPdf()}
              type="button"
            >
              {isSaving ? 'Saving PDF…' : 'Save PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-live-panel-note tool-pdf-progress" aria-live="polite">
              <strong>{progress.label}</strong>
              <progress
                max={progress.total || 1}
                value={Math.min(progress.current, progress.total)}
              />
              <span>{`${Math.min(progress.current, progress.total)} / ${progress.total}`}</span>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF page organizer</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Reorder</span>
            <span>Rotate</span>
            <span>Delete</span>
          </div>
          <p>
            Keep the page stack clean before export. This is best for scan bundles, reports, forms,
            and document packets that need a quick structural cleanup.
          </p>
          <div className="tool-live-panel-note">
            <strong>What changes are saved</strong>
            <span>
              The export keeps only the remaining pages in the chosen order, with the rotations you
              apply inside this editor.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Output pages</strong>
                <span>{source.pages.length}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Pages</h3>
          <div className="tool-pdf-page-list">
            {source.pages.map((page, index) => (
              <article className="tool-pdf-page-card" key={page.id}>
                <div className="tool-pdf-page-thumb">
                  <img
                    alt={`Preview of page ${page.pageNumber}`}
                    src={page.previewUrl}
                    style={
                      page.rotation
                        ? {
                            transform: `rotate(${page.rotation}deg)`,
                          }
                        : undefined
                    }
                  />
                </div>
                <div className="tool-pdf-page-copy">
                  <strong>{`Output ${index + 1}: source page ${page.pageNumber}`}</strong>
                  <span>
                    {page.rotation ? `Rotation applied: ${page.rotation}°` : 'No rotation applied'}
                  </span>
                  <span>Move, rotate, or delete this page before exporting the updated PDF.</span>
                </div>
                <div className="tool-pdf-page-actions">
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === 0}
                    onClick={() => movePage(page.id, -1)}
                    type="button"
                  >
                    Up
                  </button>
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === source.pages.length - 1}
                    onClick={() => movePage(page.id, 1)}
                    type="button"
                  >
                    Down
                  </button>
                  <button
                    className="tool-pdf-page-action"
                    onClick={() => rotatePage(page.id, -90)}
                    type="button"
                  >
                    Left
                  </button>
                  <button
                    className="tool-pdf-page-action"
                    onClick={() => rotatePage(page.id, 90)}
                    type="button"
                  >
                    Right
                  </button>
                  <button
                    className="tool-pdf-page-action tool-pdf-page-action-danger"
                    onClick={() => removePage(page.id)}
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
